import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { detectUiLanguage, PSEUDO, PSEUDO_ENABLED } from "./detect";
import { entry, fallbackLngMap, uiCodes } from "./languages";
import { createLazyBackend } from "./lazyBackend";
import en from "./locales/en.json";
import { migrateLegacyLocale } from "./persistence";

/** English is bundled (first paint, tests, the fallback for every key);
 *  every other locale is its own lazy chunk, fetched when selected. */
const lazyBundles = import.meta.glob<{ default: Record<string, unknown> }>([
  "./locales/*.json",
  "!./locales/en.json",
]);

/** Keep ``<html lang dir>`` in sync with the language actually shown, so
 *  screen readers pick the right voice. The full code (``zh-Hant``, not
 *  ``zh``), and ``en`` when the requested language has no bundle. */
function applyLang(): void {
  if (typeof document === "undefined") return;
  const shown = i18n.resolvedLanguage ?? i18n.language ?? "en";
  document.documentElement.setAttribute("lang", shown);
  document.documentElement.setAttribute("dir", entry(shown)?.dir ?? "ltr");
}
i18n.on("languageChanged", applyLang);

migrateLegacyLocale();

/** How the start language was chosen (``query``/``stored``/``navigator``
 *  /``default``). Only a real match may be uploaded to the account. */
export const detection = detectUiLanguage();

/** Resolves once the start language's bundle is loaded (or failed to
 *  load). main.tsx renders after it, so sv/fi users never see a flash
 *  of English. Never rejects. */
export const i18nReady: Promise<void> = i18n
  .use(createLazyBackend(lazyBundles, en))
  .use(initReactI18next)
  .init({
    lng: detection.code,
    resources: { en: { translation: en } },
    // Bundled en plus lazily loaded others.
    partialBundledLanguages: true,
    // en is usable synchronously (tests, first paint).
    initAsync: false,
    supportedLngs: [...uiCodes(), ...(PSEUDO_ENABLED ? [PSEUDO] : [])],
    nonExplicitSupportedLngs: false,
    // zh-Hans ≠ zh-Hant, pt-BR ≠ pt-PT: never collapse to the language.
    load: "currentOnly",
    fallbackLng: fallbackLngMap(),
    interpolation: { escapeValue: false },
    returnNull: false,
    react: { useSuspense: false },
  })
  .then(
    () => applyLang(),
    () => applyLang(),
  );

export default i18n;
